import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useFlags } from "@/lib/flags";
import { compareStore, useCompareIds, formatIDR } from "@/lib/compareStore";
import { getSchoolUrl } from "@/lib/slug";
import { StarRating } from "@/components/site/Stars";
import { Button } from "@/components/ui/button";
import { X, Scale } from "lucide-react";
import { TYPE_LABELS } from "@/lib/taxonomy";

const DIMS = [
  { key: "academics", label: "Akademik" },
  { key: "teachers", label: "Guru" },
  { key: "environment", label: "Lingkungan" },
  { key: "facilities", label: "Fasilitas" },
  { key: "overall", label: "Keseluruhan" },
];

export default function Compare() {
  const { school_reviews_enabled: reviewsOn } = useFlags();
  const ids = useCompareIds();
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    if (!ids.length) { setSchools([]); setLoading(false); return; }
    setLoading(true);
    api.post("/schools/compare", { ids }).then((r) => {
      setSchools(r.data.schools);
    }).finally(() => setLoading(false));
  }, [ids]);

  const remove = (id) => compareStore.remove(id);

  return (
    <div data-testid="compare-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4B5563] inline-flex items-center gap-2">
            <Scale size={12} /> Perbandingan sekolah
          </div>
          <h1 className="font-serif-display text-4xl lg:text-5xl text-[#1F2937] mt-2">Bandingkan sisi-per-sisi</h1>
          <p className="mt-3 text-[#4B5563] max-w-2xl text-sm">Pilih hingga {compareStore.MAX} sekolah dari direktori. Data yang dibandingkan disimpan lokal di browser Anda.</p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/sekolah">
            <Button className="rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="compare-add-more">
              + Tambah sekolah
            </Button>
          </Link>
          {ids.length > 0 && (
            <Button
              variant="outline"
              onClick={() => compareStore.clear()}
              data-testid="compare-clear"
              className="rounded-full border-[#EAE7E0]"
            >
              Kosongkan
            </Button>
          )}
        </div>
      </div>

      {loading && <div className="text-[#4B5563]">Memuat…</div>}

      {!loading && ids.length === 0 && (
        <div data-testid="compare-empty" className="bg-white border border-dashed border-[#EAE7E0] rounded-2xl p-12 text-center">
          <div className="text-[#4B5563] max-w-md mx-auto">Belum ada sekolah di daftar perbandingan Anda. Buka <Link to="/sekolah" className="text-[#1E3A8A] hover:text-[#E07A5F] underline">direktori</Link>, lalu klik tombol "Bandingkan" pada kartu sekolah untuk mulai.</div>
        </div>
      )}

      {!loading && schools.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-separate border-spacing-0" data-testid="compare-table">
            <thead>
              <tr>
                <th className="sticky left-0 bg-[#FAFAF8] z-10 text-left text-xs uppercase tracking-widest text-[#4B5563] p-4 w-40 border-b border-[#EAE7E0]">Aspek</th>
                {schools.map((s) => (
                  <th key={s.school_id} className="text-left align-top p-4 min-w-[240px] border-b border-[#EAE7E0]">
                    <div className="relative bg-white border border-[#EAE7E0] rounded-2xl p-4">
                      <button
                        onClick={() => remove(s.school_id)}
                        data-testid={`compare-remove-${s.school_id}`}
                        className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#F3F1ED] hover:bg-[#EAE7E0] text-[#4B5563] inline-flex items-center justify-center"
                      >
                        <X size={12} />
                      </button>
                      {s.image_url && <img src={s.image_url} alt="" className="w-full h-24 object-cover rounded-xl" />}
                      <div className="mt-3 font-serif-display text-lg text-[#1F2937] leading-tight">{s.name}</div>
                      <div className="text-xs text-[#4B5563] mt-1">{s.city}, {s.state}</div>
                      <Link to={getSchoolUrl(s)} className="mt-2 inline-block text-xs text-[#1E3A8A] hover:text-[#E07A5F]" data-testid={`compare-detail-${s.school_id}`}>
                        Buka halaman →
                      </Link>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <Row label="Jenis sekolah">
                {schools.map((s) => <Cell key={s.school_id}>{TYPE_LABELS[s.school_type] || s.school_type}</Cell>)}
              </Row>
              <Row label="Jenjang">
                {schools.map((s) => <Cell key={s.school_id}>{(s.grade_levels || []).map((g) => g.toUpperCase()).join(" / ") || "—"}</Cell>)}
              </Row>
              <Row label="Kurikulum">
                {schools.map((s) => (
                  <Cell key={s.school_id}>
                    <div className="flex flex-wrap gap-1">
                      {(s.curriculum || []).map((c) => (
                        <span key={c} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider bg-[#EEF2FF] text-[#1E3A8A] border border-[#DBEAFE]">{c}</span>
                      ))}
                      {!s.curriculum?.length && "—"}
                    </div>
                  </Cell>
                ))}
              </Row>
              <Row label="Biaya SPP">
                {schools.map((s) => (
                  <Cell key={s.school_id}>
                    <span className="font-semibold text-[#1E3A8A]">{formatIDR(s.tuition_monthly)}</span>
                  </Cell>
                ))}
              </Row>
              {reviewsOn && <Row label="Rating keseluruhan">
                {schools.map((s) => (
                  <Cell key={s.school_id}>
                    <div className="flex items-center gap-2">
                      <StarRating value={s.avg_rating || 0} />
                      <span className="text-xs text-[#4B5563]">{s.avg_rating || 0} · {s.review_count} review</span>
                    </div>
                  </Cell>
                ))}
              </Row>}
              {reviewsOn && DIMS.filter((d) => d.key !== "overall").map((d) => (
                <Row key={d.key} label={d.label}>
                  {schools.map((s) => (
                    <Cell key={s.school_id}>
                      <StarRating value={s.dim_ratings?.[d.key] || 0} />
                    </Cell>
                  ))}
                </Row>
              ))}
              <Row label="Tur virtual">
                {schools.map((s) => (
                  <Cell key={s.school_id}>
                    {s.virtual_tour_url ? (
                      <a href={s.virtual_tour_url} target="_blank" rel="noreferrer" className="text-[#1E3A8A] hover:text-[#E07A5F] text-sm underline">Buka tur ↗</a>
                    ) : "—"}
                  </Cell>
                ))}
              </Row>
              <Row label="Situs web">
                {schools.map((s) => (
                  <Cell key={s.school_id}>
                    {s.website ? <a href={s.website} target="_blank" rel="noreferrer" className="text-[#1E3A8A] hover:text-[#E07A5F] text-sm underline">Kunjungi ↗</a> : "—"}
                  </Cell>
                ))}
              </Row>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, children }) => (
  <tr>
    <td className="sticky left-0 bg-[#FAFAF8] z-10 text-sm text-[#1F2937] font-medium p-4 border-b border-[#EAE7E0]">{label}</td>
    {children}
  </tr>
);

const Cell = ({ children }) => (
  <td className="p-4 align-top text-sm text-[#1F2937] border-b border-[#EAE7E0]">{children}</td>
);
